/**
 * E3-FO-E — ELC setup + settings API client.
 *
 * Wraps `/api/v1/mod/elc/setup` (any signed-in user) and
 * `/api/v1/mod/elc/settings` (PUT needs `elc-settings.manage.org`).
 * While setup is incomplete the server refuses new typed access requests
 * and manual workflow starts with HTTP 409
 * `{detail: {code: 'elc_setup_incomplete', message}}`.
 */
import { apiFetch, ApiError } from '@/lib/api';

export const SETUP_INCOMPLETE_CODE = 'elc_setup_incomplete';

export interface SetupItem {
  key: string;
  label: string;
  done: boolean;
  required: boolean;
  detail: string;
  /** In-app path where the item is resolved (e.g. the setup page). */
  href: string;
}

export interface SetupStatus {
  complete: boolean;
  items: SetupItem[];
}

export interface ElcSettings {
  it_fulfilment_department_id: string | null;
  it_fulfilment_department_name: string | null;
  /** Active users in the group, sub-departments included. */
  it_fulfilment_member_count: number;
  updated_at: string | null;
  updated_by: string | null;
}

export const SETUP_PATH = '/modules/elc/admin/setup';

export function getElcSetup(): Promise<SetupStatus> {
  return apiFetch<SetupStatus>('/mod/elc/setup');
}

export function getElcSettings(): Promise<ElcSettings> {
  return apiFetch<ElcSettings>('/mod/elc/settings');
}

export function saveElcSettings(
  body: { it_fulfilment_department_id: string | null },
): Promise<ElcSettings> {
  return apiFetch<ElcSettings>('/mod/elc/settings', { method: 'PUT', body: JSON.stringify(body) });
}

/** The server's explanation when a gated call is refused, else null. */
export function setupIncompleteMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const detail = (err.body as { detail?: unknown } | null)?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const { code, message } = detail as { code?: unknown; message?: unknown };
  if (code !== SETUP_INCOMPLETE_CODE) return null;
  return typeof message === 'string' && message.trim()
    ? message
    : 'ELC setup is not finished yet. Ask an administrator to complete it.';
}

/** True when `err` is the 409 `elc_setup_incomplete` gate refusal. */
export function isSetupIncompleteError(err: unknown): boolean {
  return setupIncompleteMessage(err) !== null;
}

/** Message for any failed settings/setup call (422 `detail` strings included). */
export function setupErrorMessage(err: unknown, fallback = 'Request failed'): string {
  const gate = setupIncompleteMessage(err);
  if (gate) return gate;
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    if (Array.isArray(detail)) {
      const parts = detail
        .map((d) => (typeof d === 'string' ? d : (d as { msg?: unknown })?.msg))
        .filter((m): m is string => typeof m === 'string' && m.length > 0);
      if (parts.length) return parts.join('; ');
    }
    return err.message || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}
